/**
 * Wanderers (จอมยุทธ์พเนจร, lane C0-NPC phase N4; design docs/design/C0_NPC_LIFE.html §08): simulated players made the
 * way a player is — a man or a woman, a school's uniform and art, a name and a nickname (ฉายา) — each with one of six
 * personalities that sets how they play: what they go and do (meditate, hunt, the duelling ring, a market stall,
 * errands), what they say in the town chat, how often they challenge the player (at most three a day, always
 * refusable). They come online and go offline by an hours curve (busy evenings and nights, quiet mornings), fewer
 * when real players are in the map, and grow a little stronger every day. Their name plate is marked (the title
 * begins `[พเนจร]`): the game never passes them off as real people (approved default).
 *
 * Data only: `systems/wanderers.ts` runs them. Names are made up for the game (no names from fiction).
 */
import type { XY } from './layouts/plains';

export type Personality = 'hot' | 'calm' | 'sly' | 'playful' | 'shy' | 'trader';
export type WandererGoal = 'meditate' | 'hunt' | 'ring' | 'stall' | 'errand' | 'idle';

export interface PersonalityDef {
  readonly name: string;
  /** How much they like each goal. */
  readonly goals: Readonly<Record<WandererGoal, number>>;
  /** How keen they are to challenge the player at the ring (0–1 a chance). */
  readonly challenge: number;
  /** How often they speak in the town chat (1 = the usual). */
  readonly chatty: number;
  /** Town chat lines; `{name}` is the player. */
  readonly chat: readonly string[];
  /** Said challenging the player. */
  readonly dare: readonly string[];
}

export const PERSONALITIES: Readonly<Record<Personality, PersonalityDef>> = {
  hot: {
    name: 'ใจร้อน',
    goals: { meditate: 0.5, hunt: 3, ring: 3, stall: 0.2, errand: 1, idle: 0.5 },
    challenge: 0.7,
    chatty: 1.3,
    chat: ['ใครกล้าขึ้นลานประลองกับข้าบ้าง!', 'หมาป่าแถวนี้อ่อนเกินไป ไปหาตัวใหญ่กว่านี้ดีกว่า', 'วันนี้ข้าจะไต่อันดับให้ได้!'],
    dare: ['เจ้าน่ะ! มาประลองกับข้าสักตา!', 'ยืนดูอยู่ทำไม ขึ้นลานมาเลย!'],
  },
  calm: {
    name: 'ใจนิ่ง',
    goals: { meditate: 3, hunt: 1, ring: 1, stall: 0.3, errand: 1.2, idle: 1 },
    challenge: 0.25,
    chatty: 0.6,
    chat: ['ลมปราณต้องค่อยๆ หมุน รีบไปก็เท่านั้น', 'ใครหาศาลร้างกลางทุ่งเจอ นั่งสมาธิที่นั่นได้ผลดี'],
    dare: ['ขอประลองฝีมือกันสักตา ไม่ต้องรีบตอบ'],
  },
  sly: {
    name: 'เจ้าเล่ห์',
    goals: { meditate: 0.5, hunt: 1.5, ring: 1.5, stall: 2, errand: 1.5, idle: 1 },
    challenge: 0.4,
    chatty: 1,
    chat: ['ของดีราคาถูกที่แผงข้า มาก่อนได้ก่อน', 'ใครรู้ทางลัดไปผาแดงบ้าง ข้าแลกข้อมูลด้วยของดี', 'อย่าบอกใครนะ ข้าเห็นหีบสมบัติแถวป่าไผ่'],
    dare: ['ลองประลองกันดูไหม ข้าจะออมมือให้… นิดหน่อย'],
  },
  playful: {
    name: 'ขี้เล่น',
    goals: { meditate: 0.3, hunt: 1.5, ring: 1.5, stall: 0.5, errand: 1, idle: 2.5 },
    challenge: 0.5,
    chatty: 1.6,
    chat: ['ใครเห็นแมวที่ศาลาบ้าง ข้าจะเอาปลาไปให้', 'ฮ่าๆ เมื่อกี้ข้าตกน้ำที่สะพาน', 'ไปล่ากันเป็นปาร์ตี้ไหม ขาดอีกสองคน!', 'ยินดีต้อนรับ {name}! เพิ่งมาใหม่ใช่ไหม'],
    dare: ['ประลองกันเล่นๆ ไหม แพ้ก็ไม่เสียอะไร'],
  },
  shy: {
    name: 'ขี้อาย',
    goals: { meditate: 2, hunt: 2, ring: 0.5, stall: 0.3, errand: 1.5, idle: 0.8 },
    challenge: 0.1,
    chatty: 0.35,
    chat: ['…สวัสดี', 'มีใครไปทางป่าต้นน้ำบ้าง… ไปด้วยกันได้ไหม'],
    dare: ['ถ้า… ถ้าไม่รังเกียจ ประลองกับข้าได้ไหม'],
  },
  trader: {
    name: 'ค้าขาย',
    goals: { meditate: 0.3, hunt: 1, ring: 0.5, stall: 4, errand: 1, idle: 0.8 },
    challenge: 0.15,
    chatty: 1.2,
    chat: ['รับซื้อหนังหมาป่า ราคาดี ที่แผงตลาด', 'ขายยาสมุนไพรถูกๆ ต่อได้นะ', 'ใครมีเหล็กแท่งเหลือ ข้ารับแลก'],
    dare: ['ประลองแลกส่วนลดไหม ชนะข้าลดให้ห้าส่วน'],
  },
};

/** A school's look and art (the uniforms of render/looks/uniforms.ts, the arts of data/skills.ts). */
export const WANDERER_SCHOOLS: readonly { readonly art: string; readonly male: string; readonly female: string }[] = [
  { art: 'snow-sword', male: 'heroWudang', female: 'heroWudangF' },
  { art: 'bell-fist', male: 'heroShaolin', female: 'heroShaolinF' },
  { art: 'lamp-hook-sabre', male: 'heroJinyiwei', female: 'heroJinyiweiF' },
  { art: 'quiet-palm', male: 'heroJilegu', female: 'heroJileguF' },
  { art: 'ink-slate', male: 'heroJunzitang', female: 'heroJunzitangF' },
  { art: 'heat-sabre', male: 'heroBlood', female: 'heroBloodF' },
];

/** Made-up names: a family name and a given name, and a nickname of two parts. */
export const WANDERER_NAMES = {
  family: ['ฉู่', 'เยี่ยน', 'ตู้', 'หลัว', 'เผย', 'เจี่ยง', 'ซือหม่า', 'โอวหยาง', 'หนิง', 'ไป๋', 'เติ้ง', 'กู่'],
  male: ['จิ้งเฟิง', 'หยวนซาน', 'เทียนหมิง', 'ฉางเกอ', 'อวิ๋นเฉิง', 'หลี่ซวน'],
  female: ['ซินเยว่', 'ชิงหลัว', 'อวี้เหยา', 'หลิงซวง', 'เสี่ยวหาน', 'เจียอี๋'],
  nickA: ['กระบี่', 'ดาบ', 'หมัด', 'พัด', 'ทวน', 'เงา'],
  nickB: ['ลมหนาว', 'เมฆขาว', 'พันลี้', 'จันทร์เสี้ยว', 'ไร้เทียมทาน', 'หิมะแดง', 'สายฟ้า', 'ใบไผ่'],
} as const;

/** The rules. */
export const WANDERERS = {
  /** How many wanderers the plains know (big map, small map). */
  count: { big: 24, small: 8 },
  /** Share of them online by the hour (0–23): quiet mornings, busy evenings and nights. */
  curve: [0.6, 0.5, 0.4, 0.3, 0.25, 0.25, 0.3, 0.35, 0.35, 0.4, 0.4, 0.45, 0.5, 0.5, 0.5, 0.55, 0.6, 0.65, 0.75, 0.85, 0.9, 0.9, 0.8, 0.7],
  /** Seconds between two looks at who is online, and between a wanderer's goals. */
  rollEvery: 45,
  goalFor: [90, 240] as const,
  /** Seconds between two town chat lines (all of them together). */
  chatEvery: [35, 80] as const,
  /** Challenges to the player a day, and how long one stays open (s); how close to the ring the player must be (m). */
  daresPerDay: 3,
  dareOpen: 40,
  dareRing: 12,
  /** Their strength relative to the player's (spread), and how much more each in-game day. */
  strength: [0.75, 1.3] as const,
  growth: 0.02,
  /** Hunting: monsters within this of them (m), a blow every so often. */
  huntR: 8,
  huntEvery: 1.2,
  /** The name plate's mark. */
  mark: '[พเนจร]',
} as const;

/** Market stalls the traders stand at (256 m map: the players' stalls), else beside the plaza (72 m map). */
export const WANDERER_STALLS: { readonly big: readonly XY[]; readonly small: readonly XY[] } = {
  big: [
    [186, 134.5],
    [193, 134.5],
    [200, 134.5],
    [206, 134.5],
  ],
  small: [
    [8, -9],
    [11, -9],
  ],
};
